import _ from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** API */
import RegisterAppApi from 'api/application/application.api'

/** UTILS || SLICE */
import { toBuddhistYear } from 'utils/app.utils'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Grid, InputAdornment } from '@mui/material'
import HeaderCard from 'component/CardCustom/HeaderCard'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import { notiError } from 'component/notifications/notifications'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'


const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

export default function Applications() {
  const { t } = useTranslation()

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('DESC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isChecks, setIsChecks] = useState([] as any)
  const [isCheckAll, setIsCheckAll] = useState('')
  const [applications, setApplications] = useState([]);
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')


  const loadData = async () => {
    try {
      const resp = await RegisterAppApi.findAll({
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status === 'ACTIVE' ? '1' : '0'
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setApplications(resp.data)
      } else {
        setApplications([])
      }
    } catch (error) {
      notiError('เกิดข้อผิดพลาด')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }


  const onActive = () => {
    if (id) {
      RegisterAppApi.updateStatus(id, statusAlertModal === 'INACTIVE' ? '0' : '1').then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('BRANCH.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('BRANCH.MESSAGE.ERROR'))
        }
      })
    }
  }

  const handleOnCheckAll = () => {
    const hasSome = _.some(applications, (i: any) => _.some(isChecks, (j: number) => Number(i.patientId) === Number(j)))
    const hasAll = _.every(applications, (i: any) => _.some(isChecks, (j: number) => Number(i.patientId) === Number(j)))
    if (!_.isEmpty(isChecks) && !hasAll && hasSome) setIsCheckAll('indeterminate')
    else if (!_.isEmpty(isChecks) && hasAll) setIsCheckAll('checked')
    else setIsCheckAll('')
  }

  useEffect(() => {
    handleOnCheckAll()
  }, [isChecks, applications])

  const headCells = [
    { id: 'no', disablePadding: false, label: t('TABLE.NO'), width: '50px' },
    { id: 'message', disablePadding: false, label: t('APPLICATION.TABLE.CELL.CN_NUMBER'), width: '130px' },
    { id: 'branchId', disablePadding: false, label: t('APPLICATION.TABLE.CELL.PATIENT_ID'), width: '200px', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, label: t('APPLICATION.TABLE.CELL.FULL_NAME'), width: '230px' },
    { id: 'message', disablePadding: false, label: t('APPLICATION.TABLE.CELL.ID_CARD'), width: '190px' },
    { id: 'message', disablePadding: false, label: t('APPLICATION.TABLE.CELL.PHONE'), width: '130px' },
    { id: 'message', disablePadding: false, label: t('APPLICATION.TABLE.CELL.EMAIL'), width: '200px' },
    { id: 'message', disablePadding: false, label: t('APPLICATION.TABLE.CELL.CREATE_APP_AT'), width: '180px' },
    { id: 'message', disablePadding: false, label: t('APPLICATION.TABLE.CELL.UPDATED_APP_AT'), width: '190px' },
    { id: 'status', disablePadding: false, label: t('APPLICATION.TABLE.CELL.STATUS'), width: '150px' },
    { id: 'updatedBy', disablePadding: false, label: t('APPLICATION.TABLE.CELL.UPDATED_BY'), width: '180px' },
    { id: 'action', disablePadding: false, label: 'Action', width: '80px' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { patientId, cnNumber, branchCnNumber, idCard, patientFullName, phone, email, premissionApp, updatedBy, updatedAt, createdAt } = objData
    const statusBtnActive = premissionApp === '1' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = premissionApp === '0' ? true : permissions.isUpdate.disabled
    const rowCheck = _.some(isChecks, (i: number) => Number(i) === Number(patientId))
    const objRenderData = {
      key: patientId,
      id: patientId,
      obj: objData,
      rowSelect: rowCheck,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: cnNumber || '-' },
        { option: 'TEXT', align: 'left', label: branchCnNumber || '-' },
        { option: 'TEXT', align: 'left', label: patientFullName || '-' },
        { option: 'TEXT', align: 'left', label: idCard || '-' },
        { option: 'TEXT', align: 'left', label: phone || '-' },
        { option: 'TEXT', align: 'left', label: email || '-' },
        { option: 'TEXT', align: 'left', label: createdAt ? `${toBuddhistYear(moment(createdAt), 'DD/MM/YYYY HH:mm')} น.` : '10/12/2564 08:59 น.' },
        { option: 'TEXT', align: 'left', label: updatedAt ? `${toBuddhistYear(moment(updatedAt), 'DD/MM/YYYY HH:mm')} น.` : '10/12/2564 08:59 น.' },
        { option: 'STATUS', align: 'center', label: premissionApp === '1' ? 'ACTIVE' : 'INACTIVE' },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy || '-', updatedAt: updatedAt ?? '10/12/2564 08.59 น.' } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
          ]
        },
      ]
    }

    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), { branchCnNumber: branchCnNumber, name: patientFullName }, 'ACTIVE')}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), { branchCnNumber: branchCnNumber, name: patientFullName }, 'INACTIVE')}
      />
    )
  }

  const handleSubmitModal = (id: number, value: { branchCnNumber: string, name: string }, status: string) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(`${value.branchCnNumber ? value.branchCnNumber : ''} ${value.name}` || '-')
  }


  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`APPLICATION.MESSAGE.ALERT.CONFIRM_STATUS`)}
        message={t(`APPLICATION.MESSAGE.ALERT.CONFIRM_STATUS_`, { name: name, status: t(`STATUS.${statusAlertModal}`) })}
        type={'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: () => onActive(), title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 132px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('APPLICATION.HEADER')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('APPLICATION.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                  setPage(1)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={1}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type='primary'
              variant='contained'
              style={{ width: '120px' }}
              onClick={() => {
                loadData()
              }} />
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <Box sx={{
            '.MuiTable-root': {
              '.MuiTableBody-root': {
                '.MuiTableCell-root': { '&:first-of-type': { paddingLeft: 4, paddingRight: 4 } }
              }
            }
          }}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              rowCount={rowCount}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              headCells={headCells}
              rowsData={(
                _.map(applications, (item, index: number) => {
                  return renderData(item, index)
                })
              )}
              tableFixedWidth
              tableMinWidth={1600}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
